import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  UploadFile as UploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

interface Coach {
  id: string;
  coachIdCode: string;
  firstName: string;
  lastName: string;
  email: string;
  sports?: Array<{ sport: string; role: string }>;
}

interface CoachManagementProps {
  academyId: string;
}

const CoachManagement: React.FC<CoachManagementProps> = ({ academyId }) => {
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [addByCoachIdOpen, setAddByCoachIdOpen] = useState(false);
  const [coachIdSearch, setCoachIdSearch] = useState('');
  const [searchingCoach, setSearchingCoach] = useState(false);
  const [foundCoach, setFoundCoach] = useState<any>(null);
  const [editingCoach, setEditingCoach] = useState<Coach | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    coachIdCode: '', // Optional: EncycGames coachID
    specialization: '',
    experience: 0,
    country: '',
    primarySport: '',
    sports: [] as Array<{ sport: string; role: string }>,
    isFitnessCoach: false,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const specializations = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics', 'Fitness', 'Physical Education'];
  const roles = ['head_coach', 'junior_coach', 'fitness_coach'];

  useEffect(() => {
    if (academyId) {
      loadCoaches();
    } else {
      console.error('[CoachManagement] Academy ID is undefined');
    }
  }, [academyId]);

  const loadCoaches = async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Implement API endpoint to get coaches for academy
      const response = await fetch(`/api/academies/${academyId}/coaches`);
      const result = await response.json();
      
      if (result.success) {
        setCoaches(result.data || []);
      } else {
        setError(result.message || 'Failed to load coaches');
      }
    } catch (err: any) {
      console.error('Error loading coaches:', err);
      setError('Failed to load coaches');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCoach = async () => {
    const isEdit = !!editingCoach;
    
    if (!isEdit && (!formData.email || !formData.firstName || !formData.lastName)) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const url = isEdit 
        ? `/api/academies/${academyId}/coaches/${editingCoach.id}`
        : `/api/academies/${academyId}/coaches`;
      
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(isEdit ? {} : {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            coachIdCode: formData.coachIdCode || undefined,
            specialization: formData.specialization || undefined,
            experience: formData.experience || undefined,
            country: formData.country || undefined,
            primarySport: formData.primarySport || undefined,
          }),
          sports: formData.sports.length > 0 ? formData.sports : undefined,
          isFitnessCoach: formData.isFitnessCoach,
        }),
      });

      const result = await response.json();

      if (result.success) {
        if (isEdit) {
          toast.success('Coach assignments updated successfully');
        } else {
          toast.success(result.data.existed ? 'Coach added to academy successfully' : 'Coach created and added to academy successfully');
        }
        setDialogOpen(false);
        setEditingCoach(null);
        resetForm();
        loadCoaches();
      } else {
        toast.error(result.message || `Failed to ${isEdit ? 'update' : 'add'} coach`);
      }
    } catch (err: any) {
      console.error(`Error ${editingCoach ? 'updating' : 'adding'} coach:`, err);
      toast.error(`Failed to ${editingCoach ? 'update' : 'add'} coach`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('academyId', academyId);

      const response = await fetch(`/api/academies/${academyId}/coaches/upload-excel`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Successfully imported ${result.data.imported || 0} coaches`);
        setUploadDialogOpen(false);
        setSelectedFile(null);
        loadCoaches();
      } else {
        toast.error(result.message || 'Failed to upload coaches');
      }
    } catch (err: any) {
      console.error('Error uploading coaches:', err);
      toast.error('Failed to upload coaches');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCoach = (coach: Coach) => {
    setEditingCoach(coach);
    setFormData({
      email: coach.email,
      firstName: coach.firstName,
      lastName: coach.lastName,
      coachIdCode: coach.coachIdCode || '',
      specialization: '',
      experience: 0,
      country: '',
      primarySport: '',
      sports: coach.sports || [],
      isFitnessCoach: false,
    });
    setDialogOpen(true);
  };

  const handleGeneratePassword = async (coachId: string, email: string) => {
    if (!window.confirm(`Generate a new password for ${email}? The password will be displayed after generation.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/academies/${academyId}/coaches/${coachId}/generate-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Password generated successfully! Password: ${result.data.password}`);
        // Optionally copy to clipboard
        if (navigator.clipboard) {
          navigator.clipboard.writeText(result.data.password);
          toast.info('Password copied to clipboard');
        }
      } else {
        toast.error(result.message || 'Failed to generate password');
      }
    } catch (err: any) {
      console.error('Error generating password:', err);
      toast.error('Failed to generate password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCoach = async (coachId: string) => {
    if (!window.confirm('Are you sure you want to remove this coach from this academy? Note: The coach will still exist and can be added to other academies.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/academies/${academyId}/coaches/${coachId}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Coach removed from academy successfully');
        loadCoaches();
      } else {
        toast.error(result.message || 'Failed to remove coach');
      }
    } catch (err: any) {
      console.error('Error deleting coach:', err);
      toast.error('Failed to remove coach');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSport = () => {
    setFormData({
      ...formData,
      sports: [...formData.sports, { sport: '', role: '' }],
    });
  };

  const handleRemoveSport = (index: number) => {
    setFormData({
      ...formData,
      sports: formData.sports.filter((_, i) => i !== index),
    });
  };

  const handleSportChange = (index: number, field: 'sport' | 'role', value: string) => {
    const newSports = [...formData.sports];
    newSports[index] = { ...newSports[index], [field]: value };
    setFormData({ ...formData, sports: newSports });
  };

  const handleSearchByCoachId = async () => {
    if (!formData.coachIdCode) {
      toast.error('Please enter a Coach ID');
      return;
    }

    setSearchingCoach(true);
    try {
      const response = await fetch(`/api/coaches/by-id/${formData.coachIdCode}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        const coach = result.data;
        setFormData({
          ...formData,
          email: coach.email || formData.email,
          firstName: coach.firstName || coach.first_name || formData.firstName,
          lastName: coach.lastName || coach.last_name || formData.lastName,
          specialization: coach.specialization || coach.specializations || formData.specialization,
          experience: coach.experience || coach.years_of_experience || formData.experience,
          country: coach.country || formData.country,
          primarySport: coach.primarySport || coach.primary_sport || formData.primarySport,
        });
        setFoundCoach(coach);
        toast.success('Coach information loaded successfully');
      } else {
        toast.error(result.message || 'Coach not found');
        setFoundCoach(null);
      }
    } catch (err: any) {
      console.error('Error searching coach:', err);
      toast.error('Failed to search coach');
      setFoundCoach(null);
    } finally {
      setSearchingCoach(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      coachIdCode: '',
      specialization: '',
      experience: 0,
      country: '',
      primarySport: '',
      sports: [],
      isFitnessCoach: false,
    });
    setFoundCoach(null);
    setEditingCoach(null);
  };

  if (loading && coaches.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Coach Management</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Coaches can be part of multiple academies. This page shows coaches for this academy only.
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            sx={{ mr: 2 }}
          >
            Upload Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Add Coach
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Coach ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Sports</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coaches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No coaches found. Add your first coach to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              coaches.map((coach) => (
                <TableRow key={coach.id}>
                  <TableCell>{coach.coachIdCode}</TableCell>
                  <TableCell>{`${coach.firstName} ${coach.lastName}`}</TableCell>
                  <TableCell>{coach.email}</TableCell>
                  <TableCell>
                    {coach.sports && coach.sports.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {coach.sports.map((s, idx) => (
                          <Chip
                            key={idx}
                            label={s.sport}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No sports
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {coach.sports && coach.sports.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {coach.sports.map((s, idx) => (
                          <Chip
                            key={idx}
                            label={s.role.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                            size="small"
                            color={s.role === 'head_coach' ? 'primary' : s.role === 'junior_coach' ? 'secondary' : 'default'}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No roles
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleEditCoach(coach)}
                      title="Edit Coach Assignments"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="secondary"
                      onClick={() => handleGeneratePassword(coach.id, coach.email)}
                      title="Generate Password"
                    >
                      <VpnKeyIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteCoach(coach.id)}
                      title="Remove Coach from Academy"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Coach Dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingCoach ? 'Edit Coach Assignments' : 'Add Coach'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {!editingCoach && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name *"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name *"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email *"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Grid>
              </>
            )}
            {editingCoach && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Editing assignments for: <strong>{editingCoach.firstName} {editingCoach.lastName}</strong> ({editingCoach.email})
                </Alert>
              </Grid>
            )}
            {!editingCoach && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    <TextField
                      fullWidth
                      label="EncycGames Coach ID (Optional)"
                      value={formData.coachIdCode}
                      onChange={(e) => setFormData({ ...formData, coachIdCode: e.target.value })}
                      helperText="If coach is already registered, enter their Coach ID here"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleSearchByCoachId}
                      disabled={!formData.coachIdCode || searchingCoach}
                    >
                      {searchingCoach ? 'Searching...' : 'Search & Load'}
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Years of Experience"
                    type="number"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Specialization</InputLabel>
                    <Select
                      value={formData.primarySport}
                      label="Specialization"
                      onChange={(e) => setFormData({ ...formData, primarySport: e.target.value })}
                    >
                      <MenuItem value="">None</MenuItem>
                      {specializations.map((spec) => (
                        <MenuItem key={spec} value={spec}>
                          {spec}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isFitnessCoach}
                    onChange={(e) => setFormData({ ...formData, isFitnessCoach: e.target.checked })}
                  />
                }
                label="Fitness Coach (assigns to all sports as fitness_coach)"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Sport Assignments</Typography>
                <Button size="small" onClick={handleAddSport}>
                  Add Sport
                </Button>
              </Box>
              {formData.sports.map((sportAssignment, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Sport</InputLabel>
                    <Select
                      value={sportAssignment.sport}
                      label="Sport"
                      onChange={(e) => handleSportChange(index, 'sport', e.target.value)}
                    >
                      <MenuItem value="">Select Sport</MenuItem>
                      {specializations.map((sport) => (
                        <MenuItem key={sport} value={sport}>
                          {sport}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={sportAssignment.role}
                      label="Role"
                      onChange={(e) => handleSportChange(index, 'role', e.target.value)}
                    >
                      <MenuItem value="">Select Role</MenuItem>
                      {roles.map((role) => (
                        <MenuItem key={role} value={role}>
                          {role.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton onClick={() => handleRemoveSport(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
          <Button onClick={handleAddCoach} variant="contained" disabled={loading}>
            {loading ? (editingCoach ? 'Updating...' : 'Adding...') : (editingCoach ? 'Update Assignments' : 'Add Coach')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Excel Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => { setUploadDialogOpen(false); setSelectedFile(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Coaches from Excel</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Upload an Excel file (.xlsx) with coach information. The file should have columns:
              Email, First Name, Last Name, Coach ID (optional), Specialization, Experience, Country, Primary Sport, Sports (comma-separated), Roles (comma-separated).
            </Typography>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={{ marginTop: 16 }}
            />
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setSelectedFile(null); }}>Cancel</Button>
          <Button onClick={handleFileUpload} variant="contained" disabled={loading || !selectedFile}>
            {loading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CoachManagement;
